/* ============================================================================
   Merchant Setup → Zone Mapping — shared UI bits, same visual language as the
   supply-side module (SupplyHero + flat cards + section labels). The three
   pages are routed inside <SupplySideLayout>, so the scoped `.supply-scope`
   stylesheet (Badge, FileDrop, Modal, SupplyTable) applies to them as well.
   ========================================================================== */
import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Typography} from '@mui/material'
import {alpha} from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import _ from 'lodash'
import {useGetAllActiveMerchantsIdAndNameCachedQuery} from '../../redux/commonApiCalls'

export const MONO = "'JetBrains Mono', ui-monospace, monospace"

/** The scoped `.supply-scope p {margin-bottom:10px}` rule also hits MUI Typography (a <p>); undo it so spacing is ours. */
export const NO_P_MARGIN = {'& p.MuiTypography-root': {marginBottom: 0}}

/** Pincode text input: 6 digits, numeric keypad, mono font. Pair with digitsOnly in onChange. */
export const PIN_INPUT = {maxLength: 6, inputMode: 'numeric', style: {fontFamily: MONO}}
export const digitsOnly = (v) => v.replace(/\D/g, '')

/** Active merchants for the merchant pickers, name-sorted: {value: id, label: 'name (companyName)'}. */
export function useMerchantOptions() {
  const {data: merchants = [], isLoading} = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const options = useMemo(
    () =>
      _.orderBy((merchants || []).filter((m) => m.is_deleted === '0'), 'name').map((m) => ({
        value: m.id,
        label: m.companyName ? `${m.name} (${m.companyName})` : m.name,
      })),
    [merchants]
  )
  const labelOf = useCallback((oid) => options.find((o) => o.value === oid)?.label || '', [options])
  return {options, loading: isLoading, labelOf}
}

/** Flat card — identical to the m4-zones page card. */
export const CARD = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 3,
  bgcolor: 'background.paper',
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
}

export const PAGE_MAX = 1040

/* ── form controls: one 40px box for everything that sits in a row ──────────
   The scoped theme renders small MUI text fields, the autocomplete and contained
   buttons at different heights; pinning them keeps labels and boxes level. */
export const CONTROL_H = 40
/** Field label above a control — fixed height so every control's top edge lines up. */
export const LABEL = {mb: 0.75, height: 18, lineHeight: '18px', whiteSpace: 'nowrap', overflow: 'hidden'}
export const FIELD_SX = {
  '& .MuiInputBase-root': {height: CONTROL_H, fontSize: 13, borderRadius: '6px', background: '#fff'},
  '& .MuiInputBase-input': {py: 0, height: CONTROL_H, boxSizing: 'border-box'},
}
/* MUI's own `.MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall` rule (6px padding,
   and 2.5px on the input) outranks a plain `& .MuiOutlinedInput-root`, which pushed the text below
   centre inside the fixed 40px box — hence !important on the paddings. */
export const SELECT_SX = {
  '& .MuiOutlinedInput-root': {height: CONTROL_H, paddingTop: '0 !important', paddingBottom: '0 !important', paddingLeft: '12px !important',
    fontSize: 13, borderRadius: '6px', background: '#fff', alignItems: 'center'},
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {padding: '0 !important', height: CONTROL_H - 2, boxSizing: 'border-box', fontSize: 13},
}
/** Base look of the page buttons (sentence case, bold, rounded). */
export const BTN_SX = {textTransform: 'none', fontWeight: 700, borderRadius: '10px'}
/** Button that sits beside 40px fields. */
export const ROW_BTN_SX = {height: CONTROL_H, px: 2, ...BTN_SX, flexShrink: 0, whiteSpace: 'nowrap'}

/** Breadcrumb-style line above the hero title. The hero is dark, so the colour must be set here. */
export function Eyebrow({children}) {
  return (
    <Typography component='span' sx={{display: 'inline-block', fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.72)'}}>
      {children}
    </Typography>
  )
}
Eyebrow.propTypes = {children: PropTypes.node}

export function SectionLabel({children, sx}) {
  return (
    <Typography sx={{fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', ...sx}}>
      {children}
    </Typography>
  )
}
SectionLabel.propTypes = {children: PropTypes.node, sx: PropTypes.object}

/** Card with the standard header strip (title + one-line description) and padded body. */
export function ZoneCard({title, description, actions, children, sx}) {
  return (
    <Box sx={{...CARD, maxWidth: PAGE_MAX, mb: 2, overflow: 'hidden', ...sx}}>
      {title ? (
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' useFlexGap spacing={1}
          sx={{px: {xs: 2.5, md: 3}, py: 2, borderBottom: 1, borderColor: 'divider'}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: 15, fontWeight: 700, color: 'text.primary'}}>{title}</Typography>
            {description ? <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.25}}>{description}</Typography> : null}
          </Box>
          {actions ? <Box sx={{flexShrink: 0}}>{actions}</Box> : null}
        </Stack>
      ) : null}
      <Box sx={{px: {xs: 2.5, md: 3}, py: 2.5}}>{children}</Box>
    </Box>
  )
}
ZoneCard.propTypes = {title: PropTypes.node, description: PropTypes.node, actions: PropTypes.node, children: PropTypes.node, sx: PropTypes.object}

/** Inline note — info (blue) or warning (amber) — matching the m4-zones hint rows. */
export function Note({tone = 'info', children, sx}) {
  const warn = tone === 'warning'
  const Icon = warn ? WarningAmberOutlinedIcon : InfoOutlinedIcon
  return (
    <Stack direction='row' spacing={1} alignItems='flex-start'
      sx={{p: 1.5, borderRadius: 2, fontSize: 12.5, lineHeight: 1.5, color: 'text.secondary',
        bgcolor: (th) => alpha(warn ? th.palette.warning.main : th.palette.primary.main, 0.05),
        border: 1, borderColor: (th) => alpha(warn ? th.palette.warning.main : th.palette.primary.main, 0.2), ...sx}}>
      <Icon sx={{fontSize: 16, flexShrink: 0, mt: '1px', color: warn ? 'warning.main' : 'primary.main'}} />
      <span>{children}</span>
    </Stack>
  )
}
Note.propTypes = {tone: PropTypes.oneOf(['info', 'warning']), children: PropTypes.node, sx: PropTypes.object}

/** Raw API response panel (Base Zone Upload shows the upload endpoint's backend-defined reply). */
export function ResponsePanel({title, value}) {
  if (value == null || value === '') return null
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return (
    <Box sx={{mt: 2}}>
      <SectionLabel sx={{mb: 0.75}}>{title}</SectionLabel>
      <Typography component='pre' sx={{fontFamily: MONO, fontSize: 11.5, m: 0, p: 1.5, borderRadius: 2, bgcolor: 'rgba(15,23,42,0.04)', maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap'}}>
        {text}
      </Typography>
    </Box>
  )
}
ResponsePanel.propTypes = {title: PropTypes.string, value: PropTypes.any}

/** Standard error text for axios failures. */
export const apiMessage = (err) =>
  err?.response?.data?.message || err?.response?.data?.exception || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || 'Request failed'
